import { useEffect, useRef, useState, useCallback } from "react";
import { Bell, ShoppingBag, FileQuestion, AlertTriangle, Mail, Inbox } from "lucide-react";
import { adminNotificationSummary } from "../lib/api";

/**
 * Admin notification bell — top-right bell icon with a red badge that
 * counts new orders / quotes / low-stock / abandoned carts / newsletter
 * signups since the admin's last visit.
 *
 * The "last seen" cursor is persisted to localStorage as an ISO timestamp.
 * The backend's `/api/admin/notifications/summary?since=<iso>` does all
 * the counting — this component is a thin shell with polling + dropdown.
 *
 * Clicking a row jumps to the relevant admin tab via the `onJump` prop.
 */
const LS_KEY = "tc_admin_notifications_since";
const POLL_MS = 30_000;

const ITEMS = [
  { k: "new_paid_orders", label: "New paid orders", tab: "orders", icon: ShoppingBag },
  { k: "new_quotes", label: "New quote requests", tab: "quotes", icon: FileQuestion },
  { k: "low_stock", label: "Low-stock products", tab: "marketing", icon: AlertTriangle },
  { k: "abandoned_carts", label: "Abandoned carts (>1h)", tab: "abandoned", icon: Inbox },
  { k: "new_subscribers", label: "Newsletter signups", tab: "subscribers", icon: Mail },
];

export default function NotificationBell({ pw, onJump }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef(null);

  const since = (typeof window !== "undefined" && window.localStorage?.getItem(LS_KEY)) || "";

  const refresh = useCallback(async () => {
    if (!pw) return;
    setBusy(true);
    try {
      const resp = await adminNotificationSummary(pw, since);
      setData(resp);
    } catch {
      // Quietly fail — bell stays dark
    } finally {
      setBusy(false);
    }
  }, [pw, since]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, POLL_MS);
    return () => clearInterval(t);
  }, [refresh]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  const markAllSeen = () => {
    try {
      window.localStorage.setItem(LS_KEY, new Date().toISOString());
    } catch { /* ignore quota errors */ }
    setData((d) => (d ? { ...d, total: 0, new_paid_orders: 0, new_quotes: 0, low_stock: 0, abandoned_carts: 0, new_subscribers: 0 } : d));
    setOpen(false);
    setTimeout(refresh, 300);
  };

  const jumpTo = (tab) => {
    setOpen(false);
    if (typeof onJump === "function") onJump(tab);
  };

  const total = data?.total || 0;

  return (
    <div ref={wrapRef} className="relative" data-testid="admin-notification-bell-wrap">
      <button
        type="button"
        data-testid="admin-notification-bell-btn"
        onClick={() => setOpen((v) => !v)}
        className={`relative font-mono text-xs tracking-widest flex items-center gap-2 border px-3 py-2 transition-colors ${
          total > 0
            ? "border-[var(--tc-red)] text-[var(--tc-red)] hover:bg-[var(--tc-red)] hover:text-white"
            : "border-[#222] text-zinc-400 hover:text-white"
        }`}
        aria-label={`Notifications (${total})`}
      >
        <Bell size={14} />
        {total > 0 && (
          <span
            data-testid="admin-notification-badge"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--tc-red)] text-white text-[10px] font-bold flex items-center justify-center border border-black"
          >
            {total > 99 ? "99+" : total}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="admin-notification-dropdown"
          className="absolute right-0 mt-2 w-[320px] z-[80] bg-[#0a0a0a] border border-[#222] shadow-2xl"
        >
          <div className="px-4 py-3 border-b border-[#222] flex items-center justify-between">
            <span className="font-mono text-[10px] tracking-widest text-zinc-400">/ NOTIFICATIONS</span>
            <button
              data-testid="admin-notification-mark-seen"
              onClick={markAllSeen}
              className="font-mono text-[10px] tracking-widest text-zinc-500 hover:text-white"
            >
              MARK ALL SEEN
            </button>
          </div>
          <ul className="divide-y divide-[#1a1a1a]">
            {ITEMS.map(({ k, label, tab, icon: Icon }) => {
              const v = data?.[k] || 0;
              return (
                <li key={k}>
                  <button
                    type="button"
                    data-testid={`admin-notification-row-${k}`}
                    onClick={() => jumpTo(tab)}
                    className={`w-full flex items-center justify-between px-4 py-3 text-left text-sm transition-colors ${
                      v > 0 ? "text-white hover:bg-[#181818]" : "text-zinc-500 hover:bg-[#0f0f0f]"
                    }`}
                  >
                    <span className="flex items-center gap-3">
                      <Icon size={14} className={v > 0 ? "text-[var(--tc-red)]" : "text-zinc-600"} />
                      <span>{label}</span>
                    </span>
                    <span
                      className={`font-mono text-xs tracking-widest ${
                        v > 0 ? "text-[var(--tc-red)] font-bold" : "text-zinc-600"
                      }`}
                    >
                      {v}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="px-4 py-2 border-t border-[#222] text-[9px] tracking-widest font-mono text-zinc-600 text-center">
            {busy ? "REFRESHING…" : `SINCE ${(data?.since || "").slice(0, 10) || "—"} · POLLS EVERY 30s`}
          </div>
        </div>
      )}
    </div>
  );
}
